/**
 * The events the tools talk through (build spec, "Modules": tools talk only through events; if a
 * tool is off, its events never arrive and the others carry on).
 *
 * zCRM has no server, so the bus lives in the browser: an event is published by the write that
 * caused it (in the data provider, once zInspector has accepted the write) and heard by the
 * tools open in that browser. Nothing is replayed or stored. Each event's payload carries ids,
 * never whole records: a listener reads what it needs.
 *
 * | Event                 | Published by | Payload                                   |
 * |-----------------------|--------------|-------------------------------------------|
 * | lead.created          | Leads        | leadId, source                            |
 * | lead.qualified        | Leads        | leadId, dealId                            |
 * | lead.disqualified     | Leads        | leadId, reason                            |
 * | deal.stage_changed    | Pipeline     | dealId, from, to                          |
 * | deal.won              | Pipeline     | dealId, companyId                         |
 * | deal.lost             | Pipeline     | dealId, reason, closedFrom                |
 * | meeting.booked        | Booking      | meetingId, leadId, hostId, startsAt       |
 * | meeting.held          | To-dos       | meetingId, companyId                      |
 * | meeting.no_show       | To-dos       | meetingId, companyId                      |
 * | meeting.rescheduled   | To-dos       | meetingId, startsAt                       |
 *
 * Planned, with the tools that will publish them: account.created, account.merged,
 * agreement.signed, first_order, segment.changed, ticket.opened, campaign.sent.
 */
import type { Identifier } from "ra-core";

export interface ZcrmEvents {
  "lead.created": { leadId: Identifier; source: string };
  "lead.qualified": { leadId: Identifier; dealId: Identifier };
  "lead.disqualified": { leadId: Identifier; reason: string };
  "deal.stage_changed": { dealId: Identifier; from: string; to: string };
  "deal.won": { dealId: Identifier; companyId: Identifier | null };
  "deal.lost": { dealId: Identifier; reason: string; closedFrom: string };
  "meeting.booked": {
    meetingId: Identifier;
    leadId: Identifier;
    hostId: Identifier;
    startsAt: string;
  };
  "meeting.held": { meetingId: Identifier; companyId: Identifier | null };
  "meeting.no_show": { meetingId: Identifier; companyId: Identifier | null };
  "meeting.rescheduled": { meetingId: Identifier; startsAt: string };
}

export type ZcrmEventName = keyof ZcrmEvents;
type Handler<K extends ZcrmEventName> = (payload: ZcrmEvents[K]) => unknown;

const handlers = new Map<ZcrmEventName, Set<Handler<ZcrmEventName>>>();

/** Listens to an event; returns the function that stops listening. */
export const subscribe = <K extends ZcrmEventName>(
  name: K,
  handler: Handler<K>,
): (() => void) => {
  const set = handlers.get(name) ?? new Set();
  set.add(handler as Handler<ZcrmEventName>);
  handlers.set(name, set);
  return () => set.delete(handler as Handler<ZcrmEventName>);
};

/**
 * Tells every listener. A listener that fails never undoes the write that caused the event,
 * nor stops the other listeners: it is reported in the console and the rest carry on.
 */
export const publish = <K extends ZcrmEventName>(
  name: K,
  payload: ZcrmEvents[K],
): void => {
  for (const handler of handlers.get(name) ?? []) {
    Promise.resolve()
      .then(() => handler(payload))
      .catch((error: unknown) =>
        console.error(`zCRM event "${name}": a listener failed`, error),
      );
  }
};

/** Drops every listener (tests, sign-out). */
export const clearSubscriptions = (): void => handlers.clear();

/** The events a deal's stage move means: always a stage change, plus won or lost. */
export const publishDealMove = (
  deal: {
    id: Identifier;
    stage: string;
    company_id: Identifier | "";
    loss_reason: string;
    closed_from: string;
  },
  from: string,
): void => {
  publish("deal.stage_changed", { dealId: deal.id, from, to: deal.stage });
  if (deal.stage === "won") {
    publish("deal.won", {
      dealId: deal.id,
      companyId: deal.company_id || null,
    });
  }
  if (deal.stage === "lost") {
    publish("deal.lost", {
      dealId: deal.id,
      reason: deal.loss_reason,
      closedFrom: deal.closed_from || from,
    });
  }
};
